/**
 * Search API service for chat-based search functionality.
 * 
 * This module provides functions for calling the chat search API
 * and managing search sessions.
 */

import apiClient from './api';

/**
 * Perform chat-based search with natural language queries.
 * 
 * @param {string} query - Natural language search query
 * @param {string} [sessionId] - Optional session ID for conversation context
 * @param {number} [limit=10] - Maximum number of results to return
 * @param {Object} [filters] - Optional search filters
 * @returns {Promise<Object>} Search response with results
 */
export const chatSearch = async (query, sessionId = null, limit = 10, filters = null) => {
  try {
    // Use longer timeout for search - semantic search can take 30-60 seconds
    const response = await apiClient.post('/api/search/chat', {
      query,
      session_id: sessionId,
      limit,
      filters
    }, {
      timeout: 90000 // 90 second timeout for search (default is 30s)
    });
    
    return response.data;
  } catch (error) {
    console.error('Chat search failed:', error);
    
    // Extract error message from response
    const message = error.response?.data?.detail || 'Search failed';
    throw new Error(message);
  }
};

/**
 * Create a new search session.
 * 
 * @returns {Promise<Object>} New session information
 */
export const createSearchSession = async () => {
  try {
    const response = await apiClient.post('/api/search/session', {
      action: 'create'
    });
    
    return response.data;
  } catch (error) {
    console.error('Failed to create search session:', error);
    throw new Error('Failed to create search session');
  }
};

/**
 * Clear search session history.
 * 
 * @param {string} sessionId - Session ID to clear
 * @returns {Promise<Object>} Session status after clearing
 */
export const clearSearchSession = async (sessionId) => {
  try {
    const response = await apiClient.post('/api/search/session', {
      action: 'clear',
      session_id: sessionId
    });
    
    return response.data;
  } catch (error) {
    console.error('Failed to clear search session:', error);
    throw new Error('Failed to clear search session');
  }
};

/**
 * Get search session status.
 * 
 * @param {string} sessionId - Session ID to check
 * @returns {Promise<Object>} Session status information
 */
export const getSearchSessionStatus = async (sessionId) => {
  try {
    const response = await apiClient.post('/api/search/session', {
      action: 'status',
      session_id: sessionId
    });
    
    return response.data;
  } catch (error) {
    console.error('Failed to get search session status:', error);
    throw new Error('Failed to get search session status');
  }
};

/**
 * Check search service health.
 * 
 * @returns {Promise<Object>} Service health status
 */
export const getSearchHealth = async () => {
  try {
    const response = await apiClient.get('/api/search/health');
    return response.data;
  } catch (error) {
    console.error('Failed to get search health:', error);
    throw new Error('Failed to get search health');
  }
};

/**
 * Perform grounded answer search with citations.
 *
 * This endpoint generates natural language answers grounded in the user's
 * highlights and articles, with inline [n] citations.
 *
 * @param {string} query - Natural language question
 * @param {Object} [options] - Search options
 * @param {string} [options.sessionId] - Optional session ID for conversation context
 * @param {boolean} [options.includeHighlights=true] - Whether to search highlights
 * @param {boolean} [options.includeArticles=true] - Whether to search articles
 * @param {number} [options.maxSources=10] - Maximum sources for context
 * @param {Array} [options.conversationHistory] - Previous conversation turns
 * @returns {Promise<Object>} Answer response with citations
 */
export const answerSearch = async (query, options = {}) => {
  try {
    const {
      sessionId = null,
      includeHighlights = true,
      includeArticles = true,
      maxSources = 10,
      conversationHistory = null
    } = options;

    const response = await apiClient.post('/api/answer-search', {
      query,
      session_id: sessionId,
      include_highlights: includeHighlights,
      include_articles: includeArticles,
      max_sources: maxSources,
      conversation_history: conversationHistory
    }, {
      timeout: 120000 // 120 second timeout for answer generation
    });

    return response.data;
  } catch (error) {
    console.error('Answer search failed:', error);

    const message = error.response?.data?.detail || 'Answer search failed';
    throw new Error(message);
  }
};

/**
 * Create a new answer search session.
 *
 * @returns {Promise<Object>} New session information
 */
export const createAnswerSearchSession = async () => {
  try {
    const response = await apiClient.post('/api/answer-search/session', {
      action: 'create'
    });

    return response.data;
  } catch (error) {
    console.error('Failed to create answer search session:', error);
    throw new Error('Failed to create answer search session');
  }
};

/**
 * Clear answer search session history.
 *
 * @param {string} sessionId - Session ID to clear
 * @returns {Promise<Object>} Session status after clearing
 */
export const clearAnswerSearchSession = async (sessionId) => {
  try {
    const response = await apiClient.post('/api/answer-search/session', {
      action: 'clear',
      session_id: sessionId
    });

    return response.data;
  } catch (error) {
    console.error('Failed to clear answer search session:', error);
    throw new Error('Failed to clear answer search session');
  }
};